import { useEffect, useRef, useState } from 'react';
import { uploadPlanilha, UploadError, trpc, type UploadResponse, type IncrementalResumo } from '../lib/trpc';
import { EmptyState } from '../components/import/EmptyState';
import { LoadingState } from '../components/import/LoadingState';
import { UploadErrorState } from '../components/import/UploadErrorState';
import { MappingFailureState } from '../components/import/MappingFailureState';
import { SuccessState } from '../components/import/SuccessState';
import { ReviewScreen } from '../components/import/ReviewScreen';
import { HistoryList, type ImportacaoRow } from '../components/import/HistoryList';

type View =
  | { k: 'idle' }
  | { k: 'loading'; nome: string }
  | { k: 'uploadError'; msg: string; code?: string }
  | { k: 'mappingFailure'; res: UploadResponse }
  | { k: 'review'; res: UploadResponse }
  | { k: 'success'; resultado: IncrementalResumo; nome: string };

export function ImportarPage() {
  const [view, setView] = useState<View>({ k: 'idle' });
  const [history, setHistory] = useState<ImportacaoRow[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function refreshHistory() {
    try {
      const list = (await trpc.importacao.list.query()) as ImportacaoRow[];
      setHistory(list);
    } catch {
      setHistory([]);
    }
  }

  useEffect(() => {
    void refreshHistory();
  }, []);

  function pickFile() {
    fileRef.current?.click();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-selecionar o mesmo arquivo
    if (!file) return;
    await enviarArquivo(file);
  }

  async function enviarArquivo(file: File) {
    setView({ k: 'loading', nome: file.name });
    try {
      const res = await uploadPlanilha(file);
      if (res.status === 'erro') {
        if (res.requiredMissing && res.requiredMissing.length > 0) {
          setView({ k: 'mappingFailure', res });
        } else {
          setView({ k: 'uploadError', msg: res.erro ?? 'Não foi possível processar o arquivo.' });
        }
      } else {
        setView({ k: 'review', res });
      }
      void refreshHistory();
    } catch (err) {
      if (err instanceof UploadError) {
        setView({ k: 'uploadError', msg: err.message, code: err.code });
      } else {
        setView({ k: 'uploadError', msg: 'Erro inesperado no upload.' });
      }
    }
  }

  async function onConfirm(res: UploadResponse, correcoes: UploadResponse['mapeamento']) {
    const r = await trpc.importacao.confirmar.mutate({ id: res.importacaoId, correcoes });
    setView({ k: 'success', nome: res.nomeArquivo, resultado: r.resultado });
    void refreshHistory();
  }

  async function onRemover(id: string) {
    await trpc.importacao.remover.mutate({ id });
    await refreshHistory();
  }

  return (
    <div className="mx-auto max-w-4xl" data-testid="importar-page">
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="hidden"
        onChange={onFile}
      />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Importar contratos</h1>
          <p className="mt-1 text-sm text-ink-3">
            Suba sua base de contratos. O agente lê, mapeia as colunas e organiza a carteira.
          </p>
        </div>
        {view.k === 'idle' && history && history.length > 0 && (
          <button className="btn btn-primary w-full sm:w-auto" onClick={pickFile}>
            Nova importação
          </button>
        )}
      </div>

      {view.k === 'idle' && (
        <>
          {history && history.length > 0 ? (
            <HistoryList rows={history} onNova={pickFile} onRemover={onRemover} />
          ) : (
            <EmptyState onUpload={pickFile} loading={history === null} />
          )}
        </>
      )}

      {view.k === 'loading' && <LoadingState nome={view.nome} />}

      {view.k === 'uploadError' && (
        <UploadErrorState
          msg={view.msg}
          code={view.code}
          onRetry={pickFile}
          onVoltar={() => setView({ k: 'idle' })}
        />
      )}

      {view.k === 'mappingFailure' && (
        <MappingFailureState
          res={view.res}
          onRetry={pickFile}
          onVoltar={() => setView({ k: 'idle' })}
        />
      )}

      {view.k === 'review' && (
        <ReviewScreen
          res={view.res}
          onConfirm={(correcoes) => onConfirm(view.res, correcoes)}
          onCancel={() => setView({ k: 'idle' })}
        />
      )}

      {view.k === 'success' && (
        <SuccessState
          nome={view.nome}
          resultado={view.resultado}
          onNova={pickFile}
          onConcluir={() => setView({ k: 'idle' })}
        />
      )}
    </div>
  );
}
